
import { Button, Dialog, DialogContent, DialogTitle, Box, DialogActions, Typography, Divider } from '@mui/material';
import {useTranslation} from 'react-i18next';
import {SortedTable, SimpleTile} from '@dbg-ui/components';
import {makeStyles} from "@mui/styles";
import {Delete, Check, ContentCopy, Close} from '@mui/icons-material';
import {useDispatch, useSelector} from "react-redux";
import { useEffect, useState } from 'react';
import {requestApiKeys, deleteApiKey, createApiKey} from "../../stores/apikey/ApiKeyActions";
import ErrorAlert from "../components/Alerts";
import {convertfromInstant} from "../../utilities/TimeUtility";

const useStyles = makeStyles((theme) => ({
  table:{
      marginTop:20,
      minHeight: '20vh',
      maxHeight: '50vh',
      overflowY: 'scroll',
      overflowX: 'hidden'
  },
  tableCells: {
    padding: 0,
    '& .MuiTableCell-root': {
      padding: 0
    }
  },
  button:{
    backgroundColor: 'transparent',
    color: theme.palette.primary.main,
    border: `none`,
    '&:hover': {
      backgroundColor: 'transparent',
    }
  },
  keyHeader:{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
     },
  copyKeyBox:{
    display:"flex",
    justifyContent:"space-between",
    alignItems:"center"
  },
  closeButton:{
    marginLeft: 'auto',
  }
 }));

function ApiKeys({open, handleClose}) {
    const {t} = useTranslation();
    const classes = useStyles();
    const dispatch = useDispatch();

    const apiKeys= useSelector((state) => state.apiKeyReducer.apiKeys); 
    const deletedKey = useSelector((state) => state.apiKeyReducer.deleted);
    const generatedKey = useSelector((state) => state.apiKeyReducer.created);
    const error = useSelector((state) => state.apiKeyReducer.error);
    const isCreatingKey = useSelector((state) => state.apiKeyReducer.isCreatingKey);

    const [data, setData] = useState([]);
    const [createdKey, setCreatedKey] = useState(null);
    const [copied, setCopied] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [selectedKey, setSelectedKey] = useState(null);
    
    useEffect(() => {
      if(open){
        dispatch(requestApiKeys());
      }
    }, [open, deletedKey, generatedKey]);

    useEffect(() => {
      if(generatedKey){
        setCreatedKey(generatedKey.key);
      }
    }, [generatedKey]);

    useEffect(() => {
      if (apiKeys) {
        const sortDesc = [...apiKeys].sort((a, b) => new Date(b.createDate) - new Date(a.createDate));
        const data = sortDesc.map((key) => {
          return {
            a: convertfromInstant(key.createDate),
            b: convertfromInstant(key.expiryDate),
            c: <Button className={classes.button} startIcon={<Delete />} onClick={() => onDeleteKey(key)} />
          };
        });
        setData(data);
      }
    }, [apiKeys]);

    const columns = [
        {id: 'a', label: t('apiKeys.table.tableColumns.createDate')},
        {id: 'b', label: t('apiKeys.table.tableColumns.expiryDate')},
        {id: 'c', label: t('apiKeys.table.tableActions.delete')},
    ];

    function onDeleteKey(selectedKey){
      setConfirmDelete(true);
      setSelectedKey(selectedKey);
    };

    async function handleDeleteKey(selectedKey){
      dispatch(deleteApiKey(selectedKey.apiId));
      setConfirmDelete(false);
    };

    async function handleCreateKey(){
      dispatch(createApiKey());
    };

    function handleCloseKey(){
      setCreatedKey(null);
    };

    function handleCopyKey(){
      if (createdKey) {
        navigator.clipboard.writeText(createdKey);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    };

  return (<>
      <Dialog fullWidth maxWidth="md"
           open={open} 
           aria-hidden={!open} 
           aria-label='api-keys-dialog'>
        {error &&
            <SimpleTile>
              <ErrorAlert severity = "error" open={Boolean(error)} 
                  message={error.message}/>
            </SimpleTile>
          }
        <DialogTitle >
          {t('apiKeys.title')}
        </DialogTitle>
        <DialogContent>
          <SortedTable className={classes.tableCells} columns={columns} data={data}/>
        </DialogContent>
        <DialogActions>
          <Button disabled={isCreatingKey} onClick={handleCreateKey}>
            {t('apiKeys.table.tableActions.create')}
          </Button>
          <Button onClick={handleClose}>
            {t('apiKeys.table.tableActions.close')}
          </Button>
        </DialogActions>
      </Dialog>
      {createdKey && 
      <Dialog maxWidth="md" aria-hidden={!Boolean(createdKey)} aria-label='created-key-dialog'
              open={Boolean(createdKey)} onClose={handleCloseKey}>
        <DialogTitle>
          <div className={classes.keyHeader}>
            {t('apiKeys.table.message.created')}
            <Button className={`${classes.closeButton} ${classes.button}`} 
                    startIcon={<Close/>} onClick={handleCloseKey}/>
          </div>
        </DialogTitle>
        <DialogContent dividers>
          <Box className={classes.copyKeyBox}>
            <Typography component="pre">
              {createdKey ? createdKey : t('message.error')}
            </Typography >
            {createdKey && (
              <Button className= {classes.button} onClick={handleCopyKey}
                      startIcon={copied ? <Check /> : <ContentCopy />}/>
            )}
          </Box>
        </DialogContent>
      </Dialog>
      }
      <Dialog open={confirmDelete} aria-label='deleted-key-dialog'>
        <DialogTitle>
          {t('apiKeys.table.message.confirmDelete')}
        </DialogTitle>
        <DialogActions>
          <Button onClick={()=>handleDeleteKey(selectedKey)}>
            {t('apiKeys.table.message.confirm')}
          </Button>
          <Button onClick={() => setConfirmDelete(false)}>
            {t('apiKeys.table.message.cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ApiKeys;